// Admin-POS Service-Abo-Slider (08/2026): gleiche Staffelrabatt-Logik wie im
// Kundenshop (ServiceAboCalculator), aber im hellen Admin-Design. Leistung
// wählen → Menge per Slider → Staffelrabatt + Ersparnis live. Nutzt die
// auth-freien Public-Endpoints, damit es mit dem Admin-Token funktioniert.
import React, { useState, useEffect, useCallback } from 'react';
import { Package, Minus, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminServiceAboCalculator = ({ API, onSelect, customerId }) => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [quantity, setQuantity] = useState(10);
  const [inputValue, setInputValue] = useState('10');
  const [calcResult, setCalcResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/pricing/service-abo-services`);
        if (res.ok) {
          const data = await res.json();
          setServices(data.services || []);
          if (data.services?.length > 0) setSelectedService(data.services[0]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API]);

  const calculate = useCallback(async () => {
    if (!selectedService || quantity < 1) return;
    try {
      const res = await fetch(
        `${API}/public/pricing/service-abo-calculator?service_id=${selectedService.id}&quantity=${quantity}${customerId ? `&customer_id=${customerId}` : ''}`
      );
      if (res.ok) setCalcResult(await res.json());
    } catch (e) {
      console.error(e);
    }
  }, [API, selectedService, quantity, customerId]);

  useEffect(() => {
    const timer = setTimeout(() => calculate(), 250);
    return () => clearTimeout(timer);
  }, [calculate]);

  const updateQuantity = (val) => {
    const q = Math.max(1, Math.min(999, Number(val) || 1));
    setQuantity(q);
    setInputValue(String(q));
  };

  if (loading) return <div className="text-center py-6 text-gray-400">Lade Leistungen...</div>;
  if (services.length === 0)
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        Keine Leistungen für den Service-Abo-Kalkulator konfiguriert.
      </div>
    );

  const tiers = calcResult?.all_tiers || [];
  const sliderMax = (() => {
    if (!tiers.length) return 50;
    const sorted = [...tiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
    const top = sorted[sorted.length - 1];
    if (top.max_points != null) return Number(top.max_points);
    return Number(top.min_points) || 50;
  })();

  return (
    <div className="space-y-4" data-testid="admin-service-abo-calculator">
      {/* Leistung wählen */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-gray-600 text-xs font-semibold mb-2">Leistung wählen</p>
        <select
          value={selectedService?.id || ''}
          onChange={(e) => {
            const svc = services.find((s) => String(s.id) === e.target.value);
            if (svc) setSelectedService(svc);
          }}
          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-gray-800 text-sm focus:border-green-500 focus:outline-none cursor-pointer"
          data-testid="admin-service-selector"
        >
          {services.map((svc) => (
            <option key={svc.id} value={svc.id}>
              {svc.name} — CHF {svc.price_per_unit?.toFixed(2)}/Einheit
            </option>
          ))}
        </select>
      </div>

      {/* Menge */}
      {selectedService && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-600 text-sm flex items-center gap-1.5">
              <Package className="w-4 h-4 text-green-600" /> Anzahl {selectedService.name}
            </span>
            <input
              type="number"
              min="1"
              max="999"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                const v = Number(e.target.value);
                if (v >= 1) setQuantity(v);
              }}
              onBlur={() => updateQuantity(inputValue)}
              className="w-20 bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-right text-green-700 font-bold text-lg focus:border-green-500 focus:outline-none"
              data-testid="admin-service-quantity-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => updateQuantity(quantity - 5)}
              className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300 transition"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="range"
              min="1"
              max={sliderMax}
              value={Math.min(quantity, sliderMax)}
              onChange={(e) => updateQuantity(e.target.value)}
              className="flex-1 accent-green-600 h-2"
              data-testid="admin-service-quantity-slider"
            />
            <button
              onClick={() => updateQuantity(quantity + 5)}
              className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300 transition"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Preis & Rabatt */}
      {calcResult && !calcResult.error && (
        <>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-gray-400 text-xs mb-1">Normalpreis</p>
                <p className="text-gray-500 line-through text-sm" data-testid="admin-abo-full-price">
                  CHF {calcResult.full_price?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Rabatt</p>
                <p className="text-green-600 font-bold text-sm" data-testid="admin-abo-discount-pct">
                  {calcResult.discount_percent > 0 ? `-${calcResult.discount_percent}%` : '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Preis</p>
                <p className="text-green-700 font-bold text-base" data-testid="admin-abo-final-price">
                  CHF {calcResult.final_price?.toFixed(2)}
                </p>
              </div>
            </div>
            {calcResult.discount_amount > 0 && (
              <div className="mt-3 text-center bg-green-50 rounded-lg py-2 border border-green-200">
                <p className="text-green-700 text-sm font-medium">
                  Kunde spart CHF {calcResult.discount_amount?.toFixed(2)} ({calcResult.discount_percent}%)
                </p>
              </div>
            )}
            <p className="text-center text-gray-400 text-xs mt-2">
              CHF {calcResult.price_per_unit?.toFixed(2)}/Einheit
              {calcResult.tier_label ? ` · ${calcResult.tier_label}` : ''}
            </p>
          </div>

          {calcResult.recommendation && (
            <div
              className="bg-amber-50 rounded-lg p-3 border border-amber-200 cursor-pointer hover:border-amber-400 transition"
              onClick={() => updateQuantity(calcResult.recommendation.target_quantity)}
            >
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-amber-800 text-xs font-semibold">Tipp: {calcResult.recommendation.message}</p>
              </div>
            </div>
          )}

          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
            data-testid="admin-service-abo-continue-btn"
            onClick={() =>
              onSelect?.({
                id: 'service_abo',
                service_id: selectedService.id,
                service_name: selectedService.name,
                quantity,
                price: calcResult.final_price,
                full_price: calcResult.full_price,
                discount_percent: calcResult.discount_percent,
                tier_label: calcResult.tier_label,
                name: `${quantity}x ${selectedService.name}${calcResult.tier_label ? ` (${calcResult.tier_label})` : ''}`,
              })
            }
          >
            Weiter — {quantity}x {selectedService.name} für CHF {calcResult.final_price?.toFixed(2)}
          </Button>
        </>
      )}
    </div>
  );
};

export default AdminServiceAboCalculator;
